import { normalizeArenaSlots, normalizeArenaDecks } from './arena-decks.js';

// Local drafts take precedence, including an intentionally emptied deck.
// Keep existing selections editable even if older presets contain duplicates.
export function cardsAvailableForDeck(cards, decks, editingId, drafts = {}) {
    const currentId = String(editingId || '1');
    const occupied = new Set();
    let current = new Set();
    for (const deck of normalizeArenaDecks(decks)) {
        const ids = Object.values(normalizeArenaSlots(drafts?.[deck.id] ?? deck.slots));
        if (String(deck.id) === currentId) current = new Set(ids);
        else ids.forEach(id => occupied.add(id));
    }
    return (Array.isArray(cards) ? cards : []).filter(card => card &&
        (!occupied.has(String(card.id)) || current.has(String(card.id))));
}

export function chooseDeckCard(slots, cardId, targetSlot = null) {
    const next = normalizeArenaSlots(slots);
    const id = String(cardId || '');
    if (!id) return next;
    const existing = Object.keys(next).find(key => next[key] === id);
    const target = ['1', '2', '3', '4'].includes(String(targetSlot)) ? String(targetSlot) : null;
    if (target) {
        const displaced = next[target];
        if (existing && existing !== target) {
            if (displaced) next[existing] = displaced;
            else delete next[existing];
        }
        next[target] = id;
    } else if (existing) delete next[existing];
    else {
        const free = ['1', '2', '3', '4'].find(key => !next[key]);
        if (free) next[free] = id;
    }
    return normalizeArenaSlots(next);
}

export function selectWorkbenchCards(cards, { query = '', filter = 'all', sort = 'power', recovery = {} } = {}) {
    const needle = String(query).trim().toLocaleLowerCase('ru').replace(/ё/g, 'е');
    const seen = new Set();
    return (Array.isArray(cards) ? cards : []).filter(card => {
        const id = String(card.id);
        if (seen.has(id)) return false;
        seen.add(id);
        if (needle && !String(card.name || '').toLocaleLowerCase('ru').replace(/ё/g, 'е').includes(needle)) return false;
        const resting = Number(recovery[id] || 0) > 0;
        return filter === 'ready' ? !resting : filter === 'recovery' ? resting : true;
    }).sort((a, b) => {
        if (sort === 'name') return String(a.name).localeCompare(String(b.name), 'ru');
        const score = c => sort === 'attack' ? Number(c.damage ?? c.atk ?? 0)
            : sort === 'health' ? Number(c.health ?? c.hp ?? 0)
            : Number(c.damage ?? c.atk ?? 0) * 2 + Number(c.health ?? c.hp ?? 0);
        return score(b) - score(a) || String(a.id).localeCompare(String(b.id));
    });
}

export function useMinimalArenaEffects({ preference = 'auto', reducedMotion = false, saveData = false, cores = 8, memory = 8 } = {}) {
    if (preference === 'full') return reducedMotion || saveData;
    return preference === 'minimal' || reducedMotion || saveData || cores <= 4 || memory <= 4;
}

// Patch existing elements rather than destroying every image/decoder on each
// selection or HP change. Templates stay inert until genuinely new nodes enter.
const markupCache = new WeakMap();
export function patchArenaMarkup(root, markup) {
    if (!root || markupCache.get(root) === markup) return;
    const template = root.ownerDocument.createElement('template');
    template.innerHTML = markup;
    reconcileChildren(root, template.content);
    markupCache.set(root, markup);
}

function key(node) { return node.nodeType === 1 ? node.getAttribute('data-arena-key') : null; }
function reconcileChildren(parent, source) {
    const keyed = new Map(Array.from(parent.childNodes).map(node => [key(node), node]).filter(([id]) => id));
    let cursor = parent.firstChild;
    for (const next of Array.from(source.childNodes)) {
        let current = key(next) ? keyed.get(key(next)) : cursor;
        if (current && key(current) !== key(next)) current = null;
        // A decoded video poster may now be a canvas (or video fallback).
        // Keep it across HP renders even though the template starts as <img>.
        const retainedMedia = current?.nodeType === 1 && next.nodeType === 1
            && current.matches('[data-arena-source]') && next.matches('[data-arena-source]')
            && current.getAttribute('data-arena-source') === next.getAttribute('data-arena-source');
        if (retainedMedia) {
            if (current !== cursor) parent.insertBefore(current, cursor);
            cursor = current.nextSibling;
            continue;
        }
        if (!current || current.nodeType !== next.nodeType || current.nodeName !== next.nodeName) {
            current = next.cloneNode(true);
            parent.insertBefore(current, cursor);
        } else {
            if (current !== cursor) parent.insertBefore(current, cursor);
            if (current.nodeType === 3) {
                if (current.nodeValue !== next.nodeValue) current.nodeValue = next.nodeValue;
            } else if (current.nodeType === 1) {
                const sameMedia = current.matches('[data-arena-source]')
                    && current.getAttribute('data-arena-source') === next.getAttribute('data-arena-source');
                if (!sameMedia) {
                    for (const attr of Array.from(current.attributes)) if (!next.hasAttribute(attr.name)) current.removeAttribute(attr.name);
                    for (const attr of Array.from(next.attributes)) if (current.getAttribute(attr.name) !== attr.value) current.setAttribute(attr.name, attr.value);
                    reconcileChildren(current, next);
                }
            }
        }
        cursor = current.nextSibling;
    }
    while (cursor) { const next = cursor.nextSibling; cursor.remove(); cursor = next; }
}
